// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React, { FC, useCallback } from "react";
/* eslint-disable spire/fenced-imports */
import { useSwiper } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import { useArrowElement } from "@conklin/App/Components/XngCarousel/hooks";
import { ArrowPosition, ArrowType, NavigationProps } from "@conklin/App/Components/XngCarousel/models";
import IconLargeArrowLeft from "@conklin/XDL/Icons/IconLargeArrowLeft";
import IconLargeArrowRight from "@conklin/XDL/Icons/IconLargeArrowRight";
import IconPaginationArrowLeft from "@conklin/XDL/Icons/IconPaginationArrowLeft";
import IconPaginationArrowRight from "@conklin/XDL/Icons/IconPaginationArrowRight";
import { color, xClassNames } from "@conklin/XDL/Utilities";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";
import { ScrollbarOptions } from "swiper/types";

const generateArrowStyle = (
    arrowPosition: ArrowPosition,
    showOnMobile: boolean,
    arrowNavigationType: "default" | "circle",
    scrollbar: boolean | ScrollbarOptions | undefined,
) => {
    return {
        css: css`
            z-index: 10;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 0;
            outline: none !important;

            ${!showOnMobile
                ? css`
                      position: absolute;
                      ${arrowPosition === "left" ? `left: 0;` : `right: 0;`};
                      top: ${scrollbar ? `calc(50% - 12px)` : `50%`};

                      transform: translate(0, -50%);
                      ${arrowNavigationType === "circle"
                          ? css`
                                height: 52px;
                                width: 52px;
                                & > ${IconWrapper} {
                                    color: ${color("common.ui7", 0.75)};
                                    &,
                                    > svg {
                                        width: auto;
                                        height: 52px;
                                    }
                                }
                            `
                          : css`
                                background: ${color("common.ui6", 0.75)};
                                height: 78px;
                                width: 34px;
                                & > ${IconWrapper} {
                                    color: ${color("common.ui1")};
                                    &,
                                    > svg {
                                        width: auto;
                                        height: 22px;
                                    }
                                }
                            `}
                  `
                : css`
                      ${arrowPosition === "left" ? "margin-right: 16px;" : "margin-left: 16px;"}
                      & > ${IconWrapper} {
                          color: ${color("common.ui4")};
                      }
                  `}

            & > ${IconWrapper} {
                transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            }

            &:not(.swiper-button-disabled):hover {
                & > ${IconWrapper} {
                    color: ${color("common.ui10")};
                }
            }

            &:focus {
                box-shadow: none;
            }

            &.swiper-button-disabled {
                cursor: not-allowed;
                display: ${showOnMobile ? "" : "none"}; // hide the navigation if its disabled
                & > ${IconWrapper} {
                    color: ${color("common.ui12", 0.33)};
                }
            }
        `,
    };
};

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 14,
    },
    bottom: {
        src: ArrowDown,
        size: 14,
    },
    left: {
        src: IconLargeArrowLeft,
        size: 14,
    },
    right: {
        src: IconLargeArrowRight,
        size: 14,
    },
};

const circleArrowNavigationIcons = {
    top: {
        src: IconPaginationArrowRight,
        size: 52,
    },
    bottom: {
        src: IconPaginationArrowRight,
        size: 52,
    },
    left: {
        src: IconPaginationArrowLeft,
        size: 52,
    },
    right: {
        src: IconPaginationArrowRight,
        size: 52,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, scrollbar, arrowNavigationType = "default", ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } =
            arrowNavigationType === "circle"
                ? circleArrowNavigationIcons[arrowPosition]
                : arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition, showOnMobile, arrowNavigationType, scrollbar)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
                data-test-selector={`carousel-${arrowPosition}-arrow-button`}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={scrollbar ? 22 : iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
